import { useState, useRef, useEffect } from 'react';
import {
  Smile,
  Paperclip,
  Send,
  Image,
  Video,
  Music,
  MapPin,
  UserPlus,
  ListOrdered,
  MessageCircle,
  FileText,
  Camera,
  Mic,
  Square,
} from 'lucide-react';

/** Lê arquivo e retorna base64 (sem prefixo data URL) */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = typeof dataUrl === 'string' && dataUrl.startsWith('data:')
        ? dataUrl.replace(/^data:[^;]+;base64,/, '')
        : dataUrl;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function getMediatypeFromFile(file) {
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('image/')) return 'image';
  return 'document';
}

/** Emojis por categoria (estilo WhatsApp) */
const EMOJI_CATEGORIES = [
  { id: 'smileys', label: 'Rostos', emojis: '😀 😃 😄 😁 😅 😂 🤣 😊 😇 🙂 🙃 😉 😌 😍 🥰 😘 😗 😙 😚 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🤫 🤔 🤐 🤨 😐 😑 😶 😏 😒 🙄 😬 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🤧 🥵 🥶 🥴 😵 🤯 🤠 🥳 🥸 😎 🤓 🧐'.split(' ') },
  { id: 'gestures', label: 'Gestos', emojis: '👍 👎 👏 🙌 👋 🤝 🙏 ✌️ 🤞 🤟 🤘 👌 🤌 🤙 🤚 👈 👉 👆 👇 ☝️ ✋ 🤚 🖐 🖖 👋 🤙 💪 🦾 🦿 🦵 🦶 👂 🦻 👃 👀 🫀 🫁 🧠 🦷 🦴 👅 👄'.split(' ') },
  { id: 'hearts', label: 'Corações', emojis: '❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 💟 ☮️ ✝️ ☪️ 🕉 ☸️ ✡️ 🔯 🕎 ☯️ ☦️ 🛐 ⛎ ♈ ♉ ♊ ♋ ♌ ♍ ♎ ♏ ♐ ♑ ♒ ♓'.split(' ') },
  { id: 'objects', label: 'Objetos', emojis: '⌚ 📱 📲 💻 ⌨️ 🖥 🖨 🖱 🖲 🕹 🗜 💽 💾 💿 📀 📼 📷 📸 📹 🎥 📞 ☎️ 📟 📠 📺 📻 🎙 🎚 🎛 ⏱ ⏲ ⏰ 🕰 ⏳ ⏰ 🔦 🪔 💡 🔌 📡'.split(' ') },
  { id: 'nature', label: 'Natureza', emojis: '🐶 🐱 🐭 🐹 🐰 🦊 🐻 🐼 🐨 🐯 🦁 🐮 🐷 🐸 🐵 🐔 🐧 🐦 🐤 🐣 🐥 🦆 🦅 🦉 🦇 🐺 🐗 🐴 🦄 🐝 🐛 🦋 🐌 🐞 🐜 🦟 🦗 🕷'.split(' ') },
];

/** Painel de emojis estilo WhatsApp */
function EmojiPicker({ onPick, onClose }) {
  const [category, setCategory] = useState(0);
  const cat = EMOJI_CATEGORIES[category] || EMOJI_CATEGORIES[0];
  return (
    <>
      <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
      <div className="absolute left-0 bottom-full mb-2 z-50 w-[320px] max-h-[280px] flex flex-col bg-appSurface rounded-2xl shadow-2xl border border-appSurfaceBorder overflow-hidden">
        <div className="flex gap-1 p-2 border-b border-appSurfaceBorder shrink-0">
          {EMOJI_CATEGORIES.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(i)}
              className={`p-2 rounded-lg text-lg transition-colors ${i === category ? 'bg-appSurfaceBorder/50' : 'hover:bg-appSurfaceBorder/30'}`}
              title={c.label}
            >
              {c.emojis[0]}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="grid grid-cols-8 gap-1">
            {(cat.emojis || []).filter(Boolean).map((emoji, i) => (
              <button
                key={`${category}-${i}`}
                type="button"
                onClick={() => onPick(emoji)}
                className="text-2xl p-1.5 rounded-lg hover:bg-appSurfaceBorder/30 transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/** Tipos Evolution (type + evolutionPayload) alinhados ao Postman */
export const MESSAGE_TYPES = [
  { value: 'text', label: 'Texto', icon: MessageCircle },
  { value: 'media', label: 'Imagem / Vídeo / Doc', icon: Image },
  { value: 'ptv', label: 'Vídeo PTV', icon: Video },
  { value: 'audio', label: 'Áudio', icon: Music },
  { value: 'sticker', label: 'Sticker', icon: Image },
  { value: 'location', label: 'Localização', icon: MapPin },
  { value: 'contact', label: 'Contato', icon: UserPlus },
  { value: 'poll', label: 'Enquete', icon: ListOrdered },
  { value: 'list', label: 'Lista', icon: ListOrdered },
  { value: 'button', label: 'Botões', icon: MessageCircle },
];

const DEFAULT_MEDIA = { mediatype: 'image', media: '', mimetype: 'image/png', caption: '', fileName: '' };
const DEFAULT_LOCATION = { name: '', address: '', latitude: '', longitude: '' };
const DEFAULT_CONTACT = [{ fullName: '', wuid: '', phoneNumber: '' }];
const DEFAULT_POLL = { name: '', selectableCount: 1, values: '' };
const DEFAULT_LIST = { title: '', description: '', buttonText: 'Abrir', footerText: '', sectionsText: '' };
const DEFAULT_BUTTON = { title: '', description: '', footer: '', buttonsText: '' };

function getMimetype(mediatype) {
  if (mediatype === 'video') return 'video/mp4';
  if (mediatype === 'document') return 'application/pdf';
  return 'image/png';
}

/** Opções do menu de anexos (estilo WhatsApp) */
const ATTACH_MENU = [
  { id: 'document', label: 'Documento', icon: FileText, kind: 'file', accept: '*/*' },
  { id: 'photoVideo', label: 'Fotos e vídeos', icon: Image, kind: 'file', accept: 'image/*,video/*' },
  { id: 'camera', label: 'Câmera', icon: Camera, kind: 'file', accept: 'image/*,video/*', capture: true },
  { id: 'audioRecord', label: 'Áudio', icon: Music, kind: 'record' },
  { id: 'contact', label: 'Contato', icon: UserPlus, kind: 'form', value: 'contact' },
  { id: 'poll', label: 'Enquete', icon: ListOrdered, kind: 'form', value: 'poll' },
  { id: 'list', label: 'Lista', icon: ListOrdered, kind: 'form', value: 'list' },
  { id: 'button', label: 'Botões', icon: MessageCircle, kind: 'form', value: 'button' },
  { id: 'location', label: 'Localização', icon: MapPin, kind: 'form', value: 'location' },
  { id: 'sticker', label: 'Figurinha (URL)', icon: Image, kind: 'form', value: 'sticker' },
  { id: 'stickerFile', label: 'Figurinha (arquivo)', icon: Image, kind: 'fileSticker' },
  { id: 'mediaUrl', label: 'Mídia por URL', icon: Image, kind: 'form', value: 'media' },
  { id: 'ptv', label: 'Vídeo PTV', icon: Video, kind: 'form', value: 'ptv' },
];

export default function MessageComposer({ conversationId, onSendMessage, disabled, placeholder }) {
  const [messageType, setMessageType] = useState('text');
  const [text, setText] = useState('');
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);

  const [media, setMedia] = useState(DEFAULT_MEDIA);
  const [ptvVideo, setPtvVideo] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [stickerUrl, setStickerUrl] = useState('');
  const [location, setLocation] = useState(DEFAULT_LOCATION);
  const [contacts, setContacts] = useState(DEFAULT_CONTACT);
  const [poll, setPoll] = useState(DEFAULT_POLL);
  const [list, setList] = useState(DEFAULT_LIST);
  const [button, setButton] = useState(DEFAULT_BUTTON);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const documentInputRef = useRef(null);
  const photoVideoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const stickerInputRef = useRef(null);
  const textInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  const resetExtraState = () => {
    setMedia(DEFAULT_MEDIA);
    setPtvVideo('');
    setAudioUrl('');
    setStickerUrl('');
    setLocation(DEFAULT_LOCATION);
    setContacts(DEFAULT_CONTACT);
    setPoll(DEFAULT_POLL);
    setList(DEFAULT_LIST);
    setButton(DEFAULT_BUTTON);
  };

  const sendPayload = (payload) => {
    if (!payload) return;
    onSendMessage(conversationId, payload);
    setText('');
    if (messageType !== 'text') {
      setMessageType('text');
      resetExtraState();
    }
  };

  const handleFileSelect = async (e, kind) => {
    const file = e.target?.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      const mediatype = getMediatypeFromFile(file);
      const mimetype = file.type || (mediatype === 'video' ? 'video/mp4' : mediatype === 'image' ? 'image/png' : 'application/octet-stream');
      if (kind === 'sticker') {
        sendPayload({ type: 'sticker', evolutionPayload: { sticker: base64 }, text: '[figurinha]' });
      } else if (mediatype === 'image' || mediatype === 'video' || mediatype === 'document') {
        sendPayload({
          type: 'media',
          evolutionPayload: { mediatype, mimetype, media: base64, fileName: file.name },
          text: file.name || '[mídia]',
        });
      }
    } catch (err) {
      console.error('Erro ao ler arquivo', err);
    }
  };

  const startRecording = async () => {
    if (isRecording || disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (chunks.length === 0) return;
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        try {
          const base64 = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => {
              const dataUrl = r.result;
              resolve(typeof dataUrl === 'string' && dataUrl.startsWith('data:') ? dataUrl.replace(/^data:[^;]+;base64,/, '') : dataUrl);
            };
            r.onerror = () => reject(r.error);
            r.readAsDataURL(blob);
          });
          sendPayload({ type: 'audio', evolutionPayload: { audio: base64 }, text: '[áudio]' });
        } catch (e) {
          console.error('Erro ao enviar áudio', e);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingIntervalRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    } catch (err) {
      console.error('Erro ao acessar microfone', err);
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    setIsRecording(false);
    setRecordingSeconds(0);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
  };

  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const handleEmojiPick = (emoji) => {
    const input = textInputRef.current;
    if (input) {
      const start = input.selectionStart ?? text.length;
      const end = input.selectionEnd ?? text.length;
      const before = text.slice(0, start);
      const after = text.slice(end);
      setText(before + emoji + after);
      setTimeout(() => {
        input.focus();
        const pos = start + emoji.length;
        input.setSelectionRange(pos, pos);
      }, 0);
    } else {
      setText((prev) => prev + emoji);
    }
  };

  const handleAttachAction = (item) => {
    setIsTypeMenuOpen(false);
    setIsEmojiOpen(false);
    if (item.kind === 'file') {
      if (item.id === 'document') documentInputRef.current?.click();
      else if (item.id === 'photoVideo') photoVideoInputRef.current?.click();
      else if (item.id === 'camera') cameraInputRef.current?.click();
      return;
    }
    if (item.kind === 'fileSticker' && item.id === 'stickerFile') {
      stickerInputRef.current?.click();
      return;
    }
    if (item.kind === 'record') {
      startRecording();
      return;
    }
    if (item.kind === 'form' && item.value) {
      setMessageType(item.value);
    }
  };

  const canSend = () => {
    if (messageType === 'text') return (text || '').trim().length > 0;
    if (messageType === 'media') return (media.media || '').trim().length > 0;
    if (messageType === 'ptv') return (ptvVideo || '').trim().length > 0;
    if (messageType === 'audio') return (audioUrl || '').trim().length > 0;
    if (messageType === 'sticker') return (stickerUrl || '').trim().length > 0;
    if (messageType === 'location') return (location.name || '').trim() && (location.latitude || '').trim() && (location.longitude || '').trim();
    if (messageType === 'contact') return contacts.some((c) => (c.fullName || '').trim() && (c.wuid || c.phoneNumber || '').replace(/\D/g, '').length >= 10);
    if (messageType === 'poll') return (poll.name || '').trim() && (poll.values || '').trim();
    if (messageType === 'list') return (list.title || '').trim() && (list.sectionsText || '').trim();
    if (messageType === 'button') return (button.title || '').trim() && (button.buttonsText || '').trim();
    return false;
  };

  const buildPayload = () => {
    if (messageType === 'text') {
      return { text: text.trim() };
    }
    let evolutionPayload = {};
    let displayText = text?.trim() || '';

    switch (messageType) {
      case 'media':
        evolutionPayload = {
          mediatype: media.mediatype,
          mimetype: media.mimetype || getMimetype(media.mediatype),
          media: media.media.trim(),
          caption: media.caption?.trim() || undefined,
          fileName: media.fileName?.trim() || undefined,
        };
        displayText = media.caption?.trim() || '[mídia]';
        break;
      case 'ptv':
        evolutionPayload = { video: ptvVideo.trim() };
        displayText = '[vídeo PTV]';
        break;
      case 'audio':
        evolutionPayload = { audio: audioUrl.trim() };
        displayText = '[áudio]';
        break;
      case 'sticker':
        evolutionPayload = { sticker: stickerUrl.trim() };
        displayText = '[sticker]';
        break;
      case 'location':
        evolutionPayload = {
          name: location.name.trim(),
          address: (location.address || '').trim() || location.name.trim(),
          latitude: parseFloat(location.latitude) || 0,
          longitude: parseFloat(location.longitude) || 0,
        };
        displayText = location.name?.trim() || '[localização]';
        break;
      case 'contact':
        evolutionPayload = {
          contact: contacts
            .filter((c) => (c.fullName || '').trim() && (c.wuid || c.phoneNumber || '').replace(/\D/g, '').length >= 10)
            .map((c) => ({
              fullName: c.fullName.trim(),
              wuid: (c.wuid || c.phoneNumber || '').replace(/\D/g, ''),
              phoneNumber: (c.phoneNumber || c.wuid || '').trim() || (c.wuid || '').replace(/\D/g, ''),
            })),
        };
        if (evolutionPayload.contact.length === 0) return null;
        displayText = '[contato(s)]';
        break;
      case 'poll':
        evolutionPayload = {
          name: poll.name.trim(),
          selectableCount: Math.max(1, parseInt(poll.selectableCount, 10) || 1),
          values: poll.values
            .split(/[\n,;]/)
            .map((v) => v.trim())
            .filter(Boolean),
        };
        if (evolutionPayload.values.length < 2) return null;
        displayText = poll.name.trim();
        break;
      case 'list': {
        const sections = list.sectionsText
          .split(/\n---+\n/)
          .map((block) => block.trim())
          .filter(Boolean)
          .map((block) => {
            const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
            const sectionTitle = lines[0] || 'Seção';
            const rows = lines.slice(1).map((line, i) => {
              const parts = line.split('|').map((p) => p.trim());
              return {
                title: parts[0] || `Opção ${i + 1}`,
                description: parts[1] || '',
                rowId: parts[2] || `row-${i + 1}`,
              };
            });
            return { title: sectionTitle, rows };
          });
        evolutionPayload = {
          title: list.title.trim(),
          description: (list.description || '').trim() || list.title.trim(),
          buttonText: (list.buttonText || '').trim() || 'Abrir',
          footerText: (list.footerText || '').trim(),
          sections,
        };
        if (sections.length === 0) return null;
        displayText = list.title.trim();
        break;
      }
      case 'button': {
        const buttons = button.buttonsText
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((line, i) => {
            const parts = line.split('|').map((p) => p.trim());
            const displayTextBtn = parts[0] || `Botão ${i + 1}`;
            const type = (parts[1] || 'reply').toLowerCase();
            const id = parts[2] || `btn-${i + 1}`;
            const btn = { type: type === 'reply' ? 'reply' : type, displayText: displayTextBtn, id };
            if (type === 'url' && parts[3]) btn.url = parts[3];
            if (type === 'call' && parts[3]) btn.phoneNumber = parts[3].replace(/\D/g, '');
            return btn;
          });
        evolutionPayload = {
          title: button.title.trim(),
          description: (button.description || '').trim() || button.title.trim(),
          footer: (button.footer || '').trim(),
          buttons,
        };
        if (buttons.length === 0) return null;
        displayText = button.title.trim();
        break;
      }
      default:
        return null;
    }

    return { type: messageType, evolutionPayload, text: displayText };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSend()) return;
    const payload = buildPayload();
    sendPayload(payload);
  };

  const currentTypeConfig = MESSAGE_TYPES.find((t) => t.value === messageType);
  const Icon = currentTypeConfig?.icon || MessageCircle;
  const hasText = (text || '').trim().length > 0;
  const showSendButton = hasText || (messageType !== 'text' && canSend());

  return (
    <div className="bg-appSurface border-t border-appSurfaceBorder px-4 py-3 shrink-0">
      {/* Inputs de arquivo ocultos */}
      <input
        ref={documentInputRef}
        type="file"
        accept="*/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'document')}
      />
      <input
        ref={photoVideoInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'photoVideo')}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'photoVideo')}
      />
      <input
        ref={stickerInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'sticker')}
      />

      {isRecording && (
        <div className="max-w-4xl mx-auto mb-2 flex items-center gap-3 px-4 py-2 rounded-2xl bg-appSurfaceBorder/20">
          <span className="flex items-center gap-2 text-sm text-red-400">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            {Math.floor(recordingSeconds / 60)}:{(recordingSeconds % 60).toString().padStart(2, '0')}
          </span>
          <button
            type="button"
            onClick={stopRecording}
            className="ml-auto flex items-center gap-2 px-4 py-1.5 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm font-medium"
          >
            <Square size={16} />
            Parar e enviar
          </button>
        </div>
      )}

      {messageType !== 'text' && !isRecording && (
        <div className="max-w-4xl mx-auto mb-3 p-3 rounded-xl bg-appSurfaceBorder/20 border border-appSurfaceBorder">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-appTextMuted uppercase flex items-center gap-2">
              <Icon size={14} />
              {currentTypeConfig?.label}
            </span>
            <button
              type="button"
              onClick={() => { setMessageType('text'); resetExtraState(); }}
              className="text-xs text-appPrimary hover:underline"
            >
              Voltar para texto
            </button>
          </div>

          {messageType === 'media' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <select
                value={media.mediatype}
                onChange={(e) => setMedia((m) => ({ ...m, mediatype: e.target.value, mimetype: getMimetype(e.target.value) }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              >
                <option value="image">Imagem</option>
                <option value="video">Vídeo</option>
                <option value="document">Documento</option>
              </select>
              <input
                type="url"
                placeholder="URL da mídia (ou use Anexar para arquivo)"
                value={media.media}
                onChange={(e) => setMedia((m) => ({ ...m, media: e.target.value }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText placeholder:text-appTextMuted"
              />
              <input
                type="text"
                placeholder="Legenda (opcional)"
                value={media.caption}
                onChange={(e) => setMedia((m) => ({ ...m, caption: e.target.value }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText placeholder:text-appTextMuted sm:col-span-2"
              />
              <input
                type="text"
                placeholder="Nome do arquivo (opcional)"
                value={media.fileName}
                onChange={(e) => setMedia((m) => ({ ...m, fileName: e.target.value }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText placeholder:text-appTextMuted"
              />
            </div>
          )}

          {messageType === 'ptv' && (
            <input
              type="url"
              placeholder="URL do vídeo PTV"
              value={ptvVideo}
              onChange={(e) => setPtvVideo(e.target.value)}
              className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-sm text-appText placeholder:text-appTextMuted"
            />
          )}

          {messageType === 'audio' && (
            <input
              type="url"
              placeholder="URL do áudio"
              value={audioUrl}
              onChange={(e) => setAudioUrl(e.target.value)}
              className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-sm text-appText placeholder:text-appTextMuted"
            />
          )}

          {messageType === 'sticker' && (
            <input
              type="url"
              placeholder="URL do sticker"
              value={stickerUrl}
              onChange={(e) => setStickerUrl(e.target.value)}
              className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-sm text-appText placeholder:text-appTextMuted"
            />
          )}

          {messageType === 'location' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <input
                type="text"
                placeholder="Nome do lugar"
                value={location.name}
                onChange={(e) => setLocation((l) => ({ ...l, name: e.target.value }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="text"
                placeholder="Endereço"
                value={location.address}
                onChange={(e) => setLocation((l) => ({ ...l, address: e.target.value }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="text"
                placeholder="Latitude"
                value={location.latitude}
                onChange={(e) => setLocation((l) => ({ ...l, latitude: e.target.value }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="text"
                placeholder="Longitude"
                value={location.longitude}
                onChange={(e) => setLocation((l) => ({ ...l, longitude: e.target.value }))}
                className="rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
            </div>
          )}

          {messageType === 'contact' && (
            <div className="space-y-2 text-sm">
              {contacts.map((c, i) => (
                <div key={i} className="flex gap-2 flex-wrap items-center">
                  <input
                    type="text"
                    placeholder="Nome"
                    value={c.fullName}
                    onChange={(e) => setContacts((prev) => prev.map((x, j) => (j === i ? { ...x, fullName: e.target.value } : x)))}
                    className="flex-1 min-w-[100px] rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
                  />
                  <input
                    type="text"
                    placeholder="Telefone (apenas números)"
                    value={c.wuid || c.phoneNumber}
                    onChange={(e) => {
                      const v = e.target.value;
                      setContacts((prev) => prev.map((x, j) => (j === i ? { ...x, wuid: v.replace(/\D/g, ''), phoneNumber: v } : x)));
                    }}
                    className="flex-1 min-w-[100px] rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
                  />
                  {contacts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setContacts((prev) => prev.filter((_, j) => j !== i))}
                      className="text-appTextMuted hover:text-rose-500 text-xs"
                    >
                      Remover
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setContacts((prev) => [...prev, { fullName: '', wuid: '', phoneNumber: '' }])}
                className="text-xs text-appPrimary hover:underline"
              >
                + Adicionar contato
              </button>
            </div>
          )}

          {messageType === 'poll' && (
            <div className="space-y-2 text-sm">
              <input
                type="text"
                placeholder="Pergunta da enquete"
                value={poll.name}
                onChange={(e) => setPoll((p) => ({ ...p, name: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="number"
                min={1}
                placeholder="Quantas opções pode escolher (selectableCount)"
                value={poll.selectableCount}
                onChange={(e) => setPoll((p) => ({ ...p, selectableCount: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <textarea
                placeholder="Opções (uma por linha ou separadas por vírgula)"
                value={poll.values}
                onChange={(e) => setPoll((p) => ({ ...p, values: e.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText placeholder:text-appTextMuted"
              />
            </div>
          )}

          {messageType === 'list' && (
            <div className="space-y-2 text-sm">
              <input
                type="text"
                placeholder="Título da lista"
                value={list.title}
                onChange={(e) => setList((l) => ({ ...l, title: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="text"
                placeholder="Descrição"
                value={list.description}
                onChange={(e) => setList((l) => ({ ...l, description: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="text"
                placeholder="Texto do botão (ex: Abrir)"
                value={list.buttonText}
                onChange={(e) => setList((l) => ({ ...l, buttonText: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <textarea
                placeholder="Seções: primeira linha = título da seção; linhas seguintes = título | descrição | rowId. Separe seções com ---"
                value={list.sectionsText}
                onChange={(e) => setList((l) => ({ ...l, sectionsText: e.target.value }))}
                rows={4}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText placeholder:text-appTextMuted text-xs"
              />
            </div>
          )}

          {messageType === 'button' && (
            <div className="space-y-2 text-sm">
              <input
                type="text"
                placeholder="Título"
                value={button.title}
                onChange={(e) => setButton((b) => ({ ...b, title: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="text"
                placeholder="Descrição"
                value={button.description}
                onChange={(e) => setButton((b) => ({ ...b, description: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <input
                type="text"
                placeholder="Rodapé"
                value={button.footer}
                onChange={(e) => setButton((b) => ({ ...b, footer: e.target.value }))}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText"
              />
              <textarea
                placeholder="Botões: uma por linha = texto | tipo(reply/url/call) | id ou url ou telefone"
                value={button.buttonsText}
                onChange={(e) => setButton((b) => ({ ...b, buttonsText: e.target.value }))}
                rows={3}
                className="w-full rounded-lg border border-appSurfaceBorder bg-appSurface px-2 py-1.5 text-appText placeholder:text-appTextMuted text-xs"
              />
            </div>
          )}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="max-w-4xl mx-auto flex items-center gap-2 bg-appSurface px-2 py-1.5 rounded-[24px] border border-appSurfaceBorder focus-within:border-appPrimary/50 transition-colors"
      >
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => { setIsEmojiOpen(false); setIsTypeMenuOpen((v) => !v); }}
            className="p-2.5 rounded-full text-appTextMuted hover:bg-appSurfaceBorder/50 hover:text-appText transition-colors"
            aria-label="Anexar"
            title="Anexar"
          >
            <Paperclip size={22} className="rotate-90" />
          </button>
          {isTypeMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" aria-hidden onClick={() => setIsTypeMenuOpen(false)} />
              <div className="absolute left-0 bottom-full mb-2 z-50 w-64 bg-appSurface rounded-2xl shadow-2xl border border-appSurfaceBorder py-2 max-h-[70vh] overflow-y-auto">
                <div className="px-4 py-2 border-b border-appSurfaceBorder">
                  <p className="text-[11px] font-semibold text-appTextMuted uppercase tracking-wider">Anexar</p>
                </div>
                {ATTACH_MENU.map((item) => {
                  const Ico = item.icon;
                  const isForm = item.kind === 'form' && item.value === messageType;
                  const color = item.kind === 'record' ? 'text-orange-400' : 'text-appTextMuted';
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleAttachAction(item)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-appSurfaceBorder/50 text-left text-sm font-medium text-appText transition-colors ${isForm ? 'bg-appSurfaceBorder/30' : ''}`}
                    >
                      <Ico size={20} className={`shrink-0 ${color}`} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => { setIsTypeMenuOpen(false); setIsEmojiOpen((v) => !v); }}
            className={`p-2 rounded-full transition-colors ${isEmojiOpen ? 'bg-appSurfaceBorder/50 text-appPrimary' : 'text-appTextMuted hover:bg-appSurfaceBorder/50 hover:text-appText'}`}
            aria-label="Emojis"
            title="Emojis"
          >
            <Smile size={22} />
          </button>
          {isEmojiOpen && (
            <EmojiPicker
              onPick={handleEmojiPick}
              onClose={() => setIsEmojiOpen(false)}
            />
          )}
        </div>

        <input
          ref={textInputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={messageType === 'text' ? placeholder : (currentTypeConfig?.label ? `Legenda (opcional) • ${currentTypeConfig.label}` : placeholder)}
          className="flex-1 min-w-0 bg-transparent border-none focus:ring-0 text-sm py-2.5 px-1 text-appText placeholder:text-appTextMuted outline-none"
        />

        {showSendButton ? (
          <button
            type="submit"
            disabled={!canSend() || disabled}
            className="shrink-0 p-2.5 rounded-full bg-appPrimary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            aria-label="Enviar"
          >
            <Send size={22} />
          </button>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            disabled={disabled || isRecording}
            className="shrink-0 p-2.5 rounded-full bg-appPrimary text-white hover:opacity-90 disabled:opacity-50 transition-all"
            aria-label="Gravar áudio"
          >
            <Mic size={22} />
          </button>
        )}
      </form>
    </div>
  );
}
